/**
 * @fileoverview System report and dashboard definition service — binds the shared
 * `createReportDefinitionService` factory in `@firesquirrel/local-db` to `getSystemDb()`.
 * @module services/systemReportDefinitionService
 *
 * Responsibilities:
 * - Bind the shared `createReportDefinitionService` factory to `getSystemDb()` (`sourceLevel: 'system'`).
 * - Expose `watchReportDefinitions`/`watchReportDefinition` for liveQuery-backed
 *   subscriptions (raw `report_definitions` table read inside the observed
 *   function — required for Dexie change tracking).
 * - Expose CRUD, publish/archive/duplicate, and System Library exposure (`toggleLibrary`,
 *   `setReportDefinitionVisibility`).
 * - Fix G-49: `toggleLibrary` checks `dashboardConfig.widgets` for dashboards (`type === 'dashboard'`).
 * - Expose `saveSystemReportParameters` for runtime report parameter declarations.
 *
 * Dependencies:
 * - @firesquirrel/local-db (createReportDefinitionService, getSystemDb)
 * - @firesquirrel/core-domain (validateReportParameters)
 * - ./systemAuditLogService (writeAuditLog)
 * - ../apiClient (apiFetch — only for deprecated seedToTenant)
 *
 * Architecture:
 * - Local-first: Dexie is source of truth; background sync reconciles with MongoDB.
 *
 * @localFirst
 * Source of Truth: local (Dexie `report_definitions`)
 * Write Flow: Dexie write → wakeLocalWrite('report_definitions') → sync push to MongoDB
 * Sync Strategy: Optimistic write; background push
 * Conflict Resolution: Last-write-wins (server timestamp)
 *
 * Notes:
 * - `seedToTenant` is deprecated; tenants adopt system library definitions via
 *   `systemLibraryAdoptionService.adoptSystemReportDefinition()`.
 */

import { createReportDefinitionService, getSystemDb, peekSystemDb, getSystemDexieRaw } from '@firesquirrel/local-db';
import type {
  ReportDefinitionLocal,
  ReportParameter,
  ListReportDefinitionsParams,
  SystemLibraryVisibility,
} from '@firesquirrel/local-db';
import { validateReportParameters } from '@firesquirrel/core-domain';
import { writeAuditLog } from './systemAuditLogService';
import { apiFetch } from '../apiClient';

const service = createReportDefinitionService({
  getDb: async () => (peekSystemDb() ?? (await getSystemDb())) as any,
  getRaw: getSystemDexieRaw,
  sourceLevel: 'system',
  idPrefix: 'report',
  copyNameSuffix: ' (Copy)',
  auditCollection: 'systemReportDefinitions',
  writeAuditLog: (opts) => writeAuditLog(opts as Parameters<typeof writeAuditLog>[0]),
});

export type { ListReportDefinitionsParams, SystemLibraryVisibility };

export const listReportDefinitions = service.listReportDefinitions;
export const watchReportDefinitions = service.watchReportDefinitions;
export const watchReportDefinition = service.watchReportDefinition;
export const getReportDefinition = service.getReportDefinition;
export const createReportDefinition = service.createReportDefinition;
export const updateReportDefinition = service.updateReportDefinition;
export const deleteReportDefinition = service.deleteReportDefinition;
export const duplicateReportDefinition = service.duplicateReportDefinition;
export const publishReportDefinition = service.publishReportDefinition;
export const archiveReportDefinition = service.archiveReportDefinition;
export const toggleLibrary = service.toggleLibrary;
export const setReportDefinitionVisibility = service.setReportDefinitionVisibility;

/**
 * @process saveSystemReportParameters — persist a system report's runtime parameter
 *   declarations (parameters + the primitive parameterIds index array) with the
 *   shared core-domain validator.
 * Steps:
 *        1. validateReportParameters (shared validator — one implementation
 *           across hosts and the worker).
 *        2. updateReportDefinition handles audit + wakeLocalWrite.
 * Failure Handling: throws with the full validation error list.
 * Idempotency: REQUIRED — same array → same persisted state.
 * @localFirst Source of Truth: local (Dexie).
 */
export async function saveSystemReportParameters(
  reportId: string,
  parameters: ReportParameter[],
): Promise<ReportDefinitionLocal | null> {
  const errors = validateReportParameters(parameters);
  if (errors.length > 0) {
    throw new Error(`Invalid report parameters: ${errors.join('; ')}`);
  }
  return updateReportDefinition(reportId, {
    parameters,
    parameterIds: parameters.map((p) => p.parameterId),
  });
}

/**
 * @deprecated Use the library-first adoption model instead. Tenants should adopt reports
 * via `systemLibraryAdoptionService.adoptSystemReportDefinition()` after reports are
 * published and added to the library. This seed-down approach is no longer the primary
 * delivery model and will be removed in a future release.
 */
export async function seedToTenant(
  reportId: string,
  tenantIds: string[],
): Promise<{ success: boolean; seededCount: number }> {
  console.warn('[seedToTenant] Deprecated: Use library-first adoption via adoptSystemReportDefinition() instead');
  try {
    const result = await apiFetch(`/api/system/report-definitions/${reportId}/seed-to-tenant`, {
      method: 'POST',
      body: JSON.stringify({ tenantIds }),
    });

    return { success: result.success, seededCount: result.seededCount };
  } catch (error) {
    console.error('[seedToTenant] Error:', error);
    return { success: false, seededCount: 0 };
  }
}
